import { motion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  Mail,
  Shield
} from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { contactUsApi } from "../../../Redux/Redux-slices/contactSlice";
import { useNotification } from "../../../contextApi/NotificationProvider";

const ContactUs = () => {
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState(null);

  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const { loading } = useSelector((state) => state.contact);

  useEffect(() => {
    window.scrollTo(0, 0);
    generateCaptcha();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      contactReason: "support",
    },
  });

  const generateCaptcha = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    setCaptchaQuestion(result);
    setCorrectAnswer(result);
  };

  const onSubmit = async (data) => {
    if (captchaAnswer !== correctAnswer) {
      showNotification(
        "Security verification failed. Please try again",
        "error",
        3000
      );
      setCaptchaAnswer("");
      generateCaptcha();
      return;
    }

    const payload = {
      first_name: data?.firstName,
      last_name: data?.lastName,
      email: data?.email,
      mobile: data?.phoneNumber || "",
      message: `Contact Reason: ${data?.contactReason}\n\nMessage: ${data?.message}`,
      contact_us_by: "contact",
    };

    try {
      const response = await dispatch(contactUsApi(payload)).unwrap();
      if (response.message === "Your data has been successfully submitted.") {
        showNotification(
          "✅ Message sent! We'll respond within 2 hours.",
          "success",
          5000
        );
      } else {
        showNotification(
          "Something went wrong. Please try again.",
          "error",
          5000
        );
      }
    } catch (error) {
      showNotification(
        "Something went wrong. Please try again.",
        "error",
        5000
      );
    }

    reset();
    setCaptchaAnswer("");
    generateCaptcha();
  };

  const contactReasons = [
    { value: "support", label: "🛠️ Support" },
    { value: "sales", label: "💼 Sales" },
    { value: "partnership", label: "🤝 Partnership" },
    { value: "other", label: "❓ Other" },
  ];

  return (
    <>
      <Helmet>
        <title>Contact SelfSync | Support & Sales</title>
        <meta
          name="description"
          content="Contact SelfSync for support and sales inquiries. Quick response guaranteed."
        />
        <link rel="canonical" href="https://selfsync.ai/contact" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-600 to-cyan-600 text-white pt-24 pb-10 max-md:pt-20 max-md:pb-4">
          <div className="max-w-4xl mx-auto px-6 max-md:px-2 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold mb-4"
            >
              Contact Us
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl mb-4"
            >
              Support, sales, or questions - we respond in 2 hours
            </motion.p>

            {/* Quick Contact Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center items-center gap-4 mb-4 mx-auto"
            >
              <a
                href="mailto:support@selfsync.ai"
                className=" bg-gradient-to-r from-amber-400 to-amber-500 text-purple-700  p-2 md:px-8 md:py-2 rounded-lg transition-all hover:scale-105 shadow-lg w-3/4 md:w-1/2"
              >
                <div className="flex items-center justify-center gap-3">
                  <Mail className="w-6 h-6" />
                  <div className="text-left flex gap-4 items-center">
                    <div className="font-bold">Email Us</div>
                    <div className="text-sm opacity-90">
                      support@selfsync.ai
                    </div>
                  </div>
                </div>
              </a>
            </motion.div>

            <div className="flex items-center justify-center gap-6 text-sm">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                2-Hour Response
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Secure & Private
              </span>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-8">
          <div className="max-w-3xl mx-auto px-6 max-md:px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl px-4 py-4 md:p-8 shadow-xl border border-gray-100"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  We’d Love to Hear from You!
                </h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Contact Reason */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    What do you need? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 max-md:grid-cols-2 gap-3">
                    {contactReasons.map((reason) => (
                      <label
                        key={reason.value}
                        className="flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          {...register("contactReason", {
                            required: "Please select one",
                          })}
                          value={reason.value}
                          className="text-purple-600"
                        />
                        <span className="font-normal text-sm">{reason.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.contactReason && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.contactReason.message}
                    </p>
                  )}
                </div>

                {/* Name & Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("firstName", { required: "Required" })}
                      placeholder="John"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                        errors.firstName ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("lastName", { required: "Required" })}
                      placeholder="Doe"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register("email", {
                      required: "Required",
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Invalid email",
                      },
                    })}
                    placeholder="john@company.com"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    {...register("phoneNumber")}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="4"
                    {...register("message", { required: "Required" })}
                    placeholder="How can we help you?"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 resize-none ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                {/* Security Verification */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Security Check <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-100 px-4 py-2 rounded font-mono text-lg tracking-wider border-2 border-dashed">
                      {captchaQuestion}
                    </div>
                    <input
                      type="text"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Enter code"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!isValid || loading || !captchaAnswer}
                  className={`w-full font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2 ${
                    !isValid || loading || !captchaAnswer
                      ? "bg-gray-400 cursor-not-allowed"
                      : "custom-button text-white shadow-lg"
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              {/* Direct Contact */}
              <div className="mt-6 pt-4 border-t text-center">
                <p className="text-gray-600 mb-4">Need immediate help?</p>
                <div className=" flex  justify-center gap-6">
                  <a
                    href="mailto:support@selfsync.ai"
                    className="text-purple-600 hover:underline font-medium"
                  >
                    support@selfsync.ai
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactUs;
